import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import ModalPop from 'components/sales/modal/modal';
import FormSection from 'components/sales/formSection/formSection';
import { setSalesTargets, setWeeklySalesTargets, SalesTargetsApiError } from 'services/api/salesTargetsApi';
import DocumentUploadButton from 'services/utils/fileUpload/DocumentUploadButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown, faLock } from '@fortawesome/free-solid-svg-icons';
import PeriodSummary from './PeriodSummary';
import './salesTargetsListing.css';

const SAMPLE_MONTHLY_CSV_URL =
  'https://parkplus-bkt-img.parkplus.io/production/salestargets/public/sample_target_sheet_1774850703207_20260330113503748282_69450875-1d93-4b81-90ac-44726f55c48a.csv';

const SAMPLE_WEEKLY_CSV_URL =
  'https://parkplus-bkt-img.parkplus.io/production/salestargets/public/weekly_targets_1785340600954_20260729212641669980_0d3f8cc1-3c75-4f66-8c44-3ec3c31e78e7.csv';

const TARGET_TYPE = {
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
};

const toMonthStartYmd = (year, month) => {
  const y = String(year || '').trim();
  const mRaw = String(month || '').trim();
  if (!y || !mRaw) return '';
  const m = String(Number(mRaw)).padStart(2, '0');
  if (!/^\d{4}$/.test(y) || Number(m) < 1 || Number(m) > 12) return '';
  return `${y}-${m}-01`;
};

const getErrorMessage = (err) => {
  const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
  return msg && typeof msg === 'string' ? msg : 'Something went wrong. Please try again.';
};

const SetTargetsPopup = ({
  show,
  onClose,
  onSuccess,
  hasMonthlyTargetsForPeriod,
  periodMonth,
  periodYear,
  periodMonthLabel,
  periodRangeLabel,
  periodWeeks,
}) => {
  const [targetType, setTargetType] = useState(TARGET_TYPE.MONTHLY);
  const [csvUrl, setCsvUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitRowErrors, setSubmitRowErrors] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState('');

  const canUseWeekly = Boolean(hasMonthlyTargetsForPeriod);
  const isWeekly = targetType === TARGET_TYPE.WEEKLY;
  const targetStartDate = toMonthStartYmd(periodYear, periodMonth);
  const hasPeriodMonth = Boolean(targetStartDate);
  const periodLabel = periodMonthLabel || 'the current period month';

  const resetMessagesAndCsv = () => {
    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    setCsvUrl('');
  };

  useEffect(() => {
    if (!show) return;
    setTargetType(TARGET_TYPE.MONTHLY);
    setCsvUrl('');
    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    setIsUploading(false);
  }, [show]);

  useEffect(() => {
    if (!canUseWeekly && targetType === TARGET_TYPE.WEEKLY) {
      setTargetType(TARGET_TYPE.MONTHLY);
    }
  }, [canUseWeekly, targetType]);

  const handleClose = () => {
    setCsvUrl('');
    setTargetType(TARGET_TYPE.MONTHLY);
    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!csvUrl?.trim()) return;

    if (isWeekly) {
      if (!canUseWeekly) return;
      setSubmitError('');
      setSubmitRowErrors([]);
      setSubmitSuccess('');
      setIsSubmitting(true);
      try {
        await setWeeklySalesTargets({ csv_url: csvUrl.trim() });
        setSubmitSuccess('Weekly targets uploaded successfully.');
        if (typeof onSuccess === 'function') onSuccess({ type: TARGET_TYPE.WEEKLY });
      } catch (err) {
        setSubmitError(err?.message || getErrorMessage(err));
        if (err instanceof SalesTargetsApiError && err.rowErrors?.length) {
          setSubmitRowErrors(err.rowErrors);
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!hasPeriodMonth) {
      setSubmitError('Current period month is not available.');
      return;
    }

    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    setIsSubmitting(true);
    try {
      await setSalesTargets({
        csv_url: csvUrl.trim(),
        target_start_date: targetStartDate,
      });
      setSubmitSuccess('Monthly targets uploaded successfully.');
      if (typeof onSuccess === 'function') {
        onSuccess({
          type: TARGET_TYPE.MONTHLY,
          month: Number(periodMonth),
          year: Number(periodYear),
        });
      }
    } catch (err) {
      setSubmitError(err?.message || getErrorMessage(err));
      if (err instanceof SalesTargetsApiError && err.rowErrors?.length) {
        setSubmitRowErrors(err.rowErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmitMonthly = Boolean(csvUrl?.trim()) && hasPeriodMonth && !isUploading;
  const canSubmitWeekly = Boolean(csvUrl?.trim()) && canUseWeekly && !isUploading;
  const canSubmit = isWeekly ? canSubmitWeekly : canSubmitMonthly;
  const sampleUrl = isWeekly ? SAMPLE_WEEKLY_CSV_URL : SAMPLE_MONTHLY_CSV_URL;
  const sampleLabel = isWeekly ? 'Weekly sample' : 'Monthly sample';

  return (
    <ModalPop
      title="Set Targets"
      show={show}
      handleModalHide={handleClose}
      keyboard
      backdrop
      modalSize="compact"
      centered
      headerClassName="set-targets-modal-header"
      titleClassName="modal-title set-targets-modal-title"
    >
      <FormSection isLoading={isSubmitting}>
        <div className="set-targets-popup set-targets-form">
          <PeriodSummary
            monthLabel={periodMonthLabel}
            periodRangeLabel={periodRangeLabel}
            weeks={periodWeeks}
            className="mb-3"
          />

          {submitError && (
            <div className="alert alert-danger set-targets-popup-error mb-3" role="alert">
              <div className="set-targets-error-msg">{submitError}</div>
              {submitRowErrors.length > 0 && (
                <table className="set-targets-row-errors">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Email</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submitRowErrors.map((e) => (
                      <tr key={`${e.row}-${e.email}`}>
                        <td>{e.row}</td>
                        <td>{e.email}</td>
                        <td>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {submitSuccess && (
            <div className="alert alert-success set-targets-popup-error mb-3" role="alert">
              {submitSuccess}
            </div>
          )}

          <div className="set-targets-type-toggle" role="tablist" aria-label="Target type">
            <button
              type="button"
              role="tab"
              aria-selected={!isWeekly}
              className={`set-targets-type-btn ${!isWeekly ? 'is-active' : ''}`}
              onClick={() => {
                setTargetType(TARGET_TYPE.MONTHLY);
                resetMessagesAndCsv();
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isWeekly}
              className={`set-targets-type-btn ${isWeekly ? 'is-active' : ''} ${
                !canUseWeekly ? 'is-locked' : ''
              }`}
              disabled={!canUseWeekly}
              title={
                canUseWeekly
                  ? 'Set weekly targets'
                  : `Set monthly targets for ${periodLabel} first`
              }
              onClick={() => {
                if (!canUseWeekly) return;
                setTargetType(TARGET_TYPE.WEEKLY);
                resetMessagesAndCsv();
              }}
            >
              {!canUseWeekly ? (
                <FontAwesomeIcon icon={faLock} className="set-targets-type-lock" aria-hidden />
              ) : null}
              Weekly
            </button>
          </div>

          {!canUseWeekly ? (
            <p className="set-targets-unlock-note">
              Weekly unlocks after monthly targets are set for <strong>{periodLabel}</strong>.
            </p>
          ) : null}

          <div className="set-targets-form-body">
            <div className="set-targets-info-card">
              <div className="set-targets-info-card-label">Target month</div>
              <div className="set-targets-info-card-value">{periodLabel}</div>
              <div className="set-targets-info-card-hint">
                {isWeekly
                  ? 'Weekly targets apply to the current period only.'
                  : 'Monthly targets apply to the current period only.'}
              </div>
            </div>

            <div className="set-targets-upload-block">
              <div className="set-targets-upload-label">
                CSV file <span className="set-targets-weekly-required">*</span>
              </div>
              <div className="set-targets-upload-row d-flex align-items-center flex-wrap">
                <DocumentUploadButton
                  value={csvUrl}
                  onUpload={(url) => setCsvUrl(url)}
                  onUploadingChange={setIsUploading}
                  accept=".csv,text/csv"
                  uploadOptions={{ service_name: 'salestargets', doc_type: 'document' }}
                  buttonLabel={isUploading ? 'Uploading…' : 'Upload CSV'}
                  uploadedLabel="✓ CSV uploaded"
                  required
                />
                <a
                  href={sampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm set-targets-sample-csv-icon d-inline-flex align-items-center justify-content-center"
                  title={`Download ${sampleLabel.toLowerCase()} CSV`}
                  aria-label={`Download ${sampleLabel.toLowerCase()} CSV`}
                >
                  <FontAwesomeIcon icon={faFileArrowDown} aria-hidden />
                  <span className="ml-1">{sampleLabel}</span>
                </a>
              </div>
            </div>
          </div>

          <div className="set-targets-form-footer">
            <Button
              className="btn btn-primary btn-sm"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting
                ? 'Submitting...'
                : isWeekly
                  ? 'Set weekly targets'
                  : 'Set monthly targets'}
            </Button>
          </div>
        </div>
      </FormSection>
    </ModalPop>
  );
};

SetTargetsPopup.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  hasMonthlyTargetsForPeriod: PropTypes.bool,
  periodMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  periodYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  periodMonthLabel: PropTypes.string,
  periodRangeLabel: PropTypes.string,
  periodWeeks: PropTypes.arrayOf(
    PropTypes.shape({
      week_start: PropTypes.string,
      week_end: PropTypes.string,
    })
  ),
};

SetTargetsPopup.defaultProps = {
  onSuccess: undefined,
  hasMonthlyTargetsForPeriod: false,
  periodMonth: '',
  periodYear: '',
  periodMonthLabel: '',
  periodRangeLabel: '',
  periodWeeks: [],
};

export default SetTargetsPopup;
